import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { customersAPI, ordersAPI, salesAPI } from '../../services/api';
import { useCompany } from '../CompanyContext';
import { Customer, Order, Sale, CustomerStatus, CustomerType } from '../../types';
import {
  ArrowLeftIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  UserIcon,
  MapPinIcon,
  EnvelopeIcon,
  PhoneIcon,
  GlobeAltIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ShoppingCartIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';

const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { selectedCompany } = useCompany();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id && selectedCompany) {
      loadCustomerData();
    }
  }, [id, selectedCompany]);

  const loadCustomerData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      // Load customer details
      const customerData = await customersAPI.getById(id);
      setCustomer(customerData);

      // Load customer orders (filter by query param)
      const ordersData = await ordersAPI.getByCustomer(id);
      setOrders(ordersData);

      // Load customer sales (use dedicated endpoint)
      const salesData = await salesAPI.getByCustomer(id);
      setSales(salesData);
    } catch (err) {
      setError('Failed to load customer data');
      console.error('Error loading customer data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: CustomerStatus) => {
    switch (status) {
      case CustomerStatus.ACTIVE:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case CustomerStatus.INACTIVE:
        return <XCircleIcon className="h-5 w-5 text-gray-500" />;
      case CustomerStatus.BLOCKED:
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <CheckCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: CustomerStatus) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case CustomerStatus.ACTIVE:
        return `${baseClasses} bg-green-100 text-green-800`;
      case CustomerStatus.INACTIVE:
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case CustomerStatus.BLOCKED:
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getTypeIcon = (type: CustomerType) => {
    switch (type) {
      case CustomerType.BUSINESS:
        return <BuildingOfficeIcon className="h-4 w-4 text-blue-600" />;
      case CustomerType.INDIVIDUAL:
        return <UserIcon className="h-4 w-4 text-green-600" />;
      default:
        return <UserIcon className="h-4 w-4 text-gray-600" />;
    }
  };

  const formatCurrency = (amount: number) => {
    const currency = selectedCompany?.defaultCurrency?.code || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleDelete = async () => {
    if (!customer || !window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    try {
      await customersAPI.delete(customer.id);
      // Navigate back to customers list
      window.location.href = '/customers';
    } catch (err) {
      setError('Failed to delete customer');
      console.error('Error deleting customer:', err);
    }
  };

  if (!selectedCompany) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">No company selected</h3>
        <p className="mt-1 text-sm text-gray-500">Please select a company to view customer details.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">Customer not found</h3>
        <p className="mt-1 text-sm text-gray-500">The customer you're looking for doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/customers"
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{customer.name}</h1>
            <p className="text-gray-600">Customer Details</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Link
            to={`/customers/${customer.id}/edit`}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit
          </Link>
          <button
            onClick={handleDelete}
            className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Customer Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              <div className="flex items-center space-x-2">
                {getTypeIcon(customer.type)}
                <span className="text-sm text-gray-600">{customer.type}</span>
                <span className={getStatusBadge(customer.status)}>
                  {customer.status}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="space-y-3">
                {customer.email && (
                  <div className="flex items-center">
                    <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">{customer.email}</span>
                  </div>
                )}
                {customer.phone && (
                  <div className="flex items-center">
                    <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">{customer.phone}</span>
                  </div>
                )}
                {customer.website && (
                  <div className="flex items-center">
                    <GlobeAltIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <a 
                      href={customer.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:text-primary-800"
                    >
                      {customer.website}
                    </a>
                  </div>
                )}
              </div>
              
              <div className="space-y-3">
                {customer.address && (
                  <div className="flex items-start">
                    <MapPinIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    <div className="text-sm text-gray-900">
                      <div>{customer.address}</div>
                      {customer.city && customer.state && (
                        <div>{customer.city}, {customer.state} {customer.postalCode}</div>
                      )}
                      {customer.country && <div>{customer.country}</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Business Information (if business customer) */}
          {customer.type === CustomerType.BUSINESS && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <BuildingOfficeIcon className="h-5 w-5 mr-2" />
                Business Information
              </h3>
              
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="space-y-3">
                  {customer.taxId && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">Tax ID:</span>
                      <div className="text-sm text-gray-900">{customer.taxId}</div>
                    </div>
                  )}
                  {customer.contactPerson && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">Contact Person:</span>
                      <div className="text-sm text-gray-900">{customer.contactPerson}</div>
                    </div>
                  )}
                </div>
                
                <div className="space-y-3">
                  {customer.contactEmail && (
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{customer.contactEmail}</span>
                    </div>
                  )}
                  {customer.contactPhone && (
                    <div className="flex items-center">
                      <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{customer.contactPhone}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Financial Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 mr-2" />
              Financial Information
            </h3>
            
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div>
                <span className="text-sm font-medium text-gray-700">Credit Limit</span>
                <div className="text-lg font-semibold text-gray-900">
                  {formatCurrency(customer.creditLimit)}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Current Balance</span>
                <div className={`text-lg font-semibold ${customer.currentBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(customer.currentBalance)}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Payment Terms</span>
                <div className="text-sm text-gray-900">
                  {customer.paymentTerms || 'Not specified'}
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {customer.notes && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2" />
                Notes
              </h3>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{customer.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Orders</span>
                <span className="text-lg font-semibold text-gray-900">{orders.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Sales</span>
                <span className="text-lg font-semibold text-gray-900">{sales.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Revenue</span>
                <span className="text-lg font-semibold text-green-600">
                  {formatCurrency(sales.reduce((sum, sale) => sum + sale.total, 0))}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full Lists for Orders and Sales */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* All Orders for Customer */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <ShoppingCartIcon className="h-5 w-5 mr-2" />
              Orders for this customer ({orders.length})
            </h3>
            <Link to="/orders" className="text-sm text-primary-600 hover:text-primary-800">Orders</Link>
          </div>
          {orders.length === 0 ? (
            <p className="text-sm text-gray-500">No orders found</p>
          ) : (
            <div className="space-y-2 max-h-[480px] overflow-auto pr-1">
              {orders.map((order) => (
                <Link key={order.id} to={`/orders/${order.id}`} className="block">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                    <div>
                      <div className="text-sm font-medium text-gray-900">#{order.id.slice(0, 8)}</div>
                      <div className="text-xs text-gray-500">{formatDate(order.createdAt)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">{formatCurrency(order.totalAmount)}</div>
                      <div className="text-xs text-gray-500">{order.status}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* All Sales for Customer */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <CreditCardIcon className="h-5 w-5 mr-2" />
              Sales for this customer ({sales.length})
            </h3>
            <Link to="/sales" className="text-sm text-primary-600 hover:text-primary-800">Sales</Link>
          </div>
          {sales.length === 0 ? (
            <p className="text-sm text-gray-500">No sales found</p>
          ) : (
            <div className="space-y-2 max-h-[480px] overflow-auto pr-1">
              {sales.map((sale) => (
                <Link key={sale.id} to={`/sales/${sale.id}`} className="block">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{sale.invoiceNumber}</div>
                      <div className="text-xs text-gray-500">{formatDate(sale.createdAt)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">{formatCurrency(sale.total)}</div>
                      <div className="text-xs text-gray-500">{sale.status}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;
